import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { debounce } from 'lodash';

import { useTheme } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';

import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ChangeHistoryIcon from '@material-ui/icons/ChangeHistory';
import PermIdentityIcon from '@material-ui/icons/PermIdentity';
import ChromeReaderModeIcon from '@material-ui/icons/ChromeReaderMode';
import LocationCityIcon from '@material-ui/icons/LocationCity';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import BarChartIcon from '@material-ui/icons/BarChart';

import * as math from '_core/utils/math';
import * as api from '_core/utils/api';
import { useVisitorInfo } from '_core/utils/visitorInfo';
import { useGlobal } from '_core/hooks/useGlobal';
import { withLang } from '_core/hocs/withLang';
import { Flex } from '_core/components/Flex';

import { useStyle } from './style';
import * as values from './value';
import FilterPeriod from './FilterPeriod';
import FilterOptions from './FilterOptions';
import Header from './Header';
import Rows from './Rows';
import RowInfo from './RowInfo';


const DEFAULT_COLUMNS = [
    { type: 'level', name: 'level', title: '$backoffice.admin.reports.visstrwrk.level', width: 18, is_show_table: true },
    { type: 'login', name: 'login', title: '$global.field.login', width: 30, is_show_table: true },
    { type: 'fullname', name: 'fullname', title: '$global.field.fullname', width: 30, is_show_table: true },
    { type: 'locality', name: 'locality', title: '$global.field.city', width: 24, is_show_table: true }
  ];


const getCellStyles = (theme, column, inner) => inner ? ({
  width: theme.spacing(column.width),
  minWidth: theme.spacing(column.width)
}) : ({
  width: theme.spacing(column.currentWidth),
  minWidth: theme.spacing(column.currentWidth)
});


export const getClassicVisitorColumn = async (visitorInfo) => {
  let columns;
  
  try {
    columns = await api.getClassicVisitorColumn();
  } catch (e) {
    console.error(e);
    
    columns = DEFAULT_COLUMNS;
  }
  
  columns = columns.filter((column) => visitorInfo.inGroup(column.visitor_group)).map((column) => {
    const initcapName = column.type.substring(0, 1).toUpperCase() + column.type.substring(1).toLowerCase();
    
    column = {
      ...column,
      
      ValueComponent: values[initcapName + 'Value'] || values.DefaultValue,
      FilterComponent: values[initcapName + 'Filter'] || values.DefaultFilter,
      TagsComponent: values[initcapName + 'Tags'] || values.DefaultTags,
      getValueApiParam: values['get' + initcapName + 'ApiParam'] || values.getDefaultApiParam,
      getValueExport: values['get' + initcapName + 'ExportValue'] || values.getDefaultExportValue,
      
      nameSplit: column.name.split(':').map((value, index) => index ? value : value.split('.')),
      
      currentWidth: column.width
    };
    
    const getValueData = values['get' + initcapName + 'Data'];
    
    if (getValueData)
      column.valueData = getValueData();
    
    return column;
  });
  
  for (const index in columns) {
    const column = columns[index];
    
    if (column.valueData) {
      try {
        column.valueData = await column.valueData;
      } catch (e) {
        console.error(e);
      }
    }
  }
  
  return columns;
};

export const RawLinear = (props) => {
  const {
    langInfo,
    getLangString: l
  } = props;
  
  const [tableColumns, setTableColumns] = React.useState([]);
  const [detailsColumns, setDetailsColumns] = React.useState([]);
  const [exportColumns, setExportColumns] = React.useState([]);
  const [data, setData] = React.useState({});
  const [selected, setSelected] = React.useState();
  const [localityIds, setLocalityIds] = React.useState([]);
  
  const [period, setPeriod] = React.useState();
  const [filters, setFilters] = React.useState({});
  const [order, setOrder] = React.useState('-time');
  const [limit, setLimit] = React.useState(20);
  
  const [rowInfo, setRowInfo] = React.useState(null);
  
  const theme = useTheme();
  const classes = useStyle();
  
  const visitorInfo = useVisitorInfo();
  
  const {
    request: { queue },
    config: { API_METHOD }
  } = useGlobal();
  
  const visitorId = visitorInfo.info.id;
  
  const getRows = React.useCallback((parentId, canFetch) => {
    if (!visitorId || !period)
      return null;
    
    if (data.id !== visitorId || data.period !== period.id || data.filters !== filters) {
      data.token = null;
      data.id = visitorId;
      data.period = period.id;
      data.filters = filters;
      data.hits = {};
      data.map = {};
    }
    
    let result = data.map[parentId];
    
    if (canFetch && (result === undefined || result && (result.order !== order || result.limit !== limit))) {
      data.map[parentId] = result = {
        order,
        limit,
        
        isLoading: true,
        
        hits: null,
        list: [],
        
        hasNextBar: false,
        hasNext: false,
        isNextLoading: false,
        hasPrev: false,
        
        next: async () => {
          if (result.isNextLoading)
            return;
          
          result.isNextLoading = true;
          
          if (!data.token)
            data.token = await api.getClientTokenGenerate(visitorId);
          
          try {
            const param = {
              token: data.token,
              parent: parentId !== visitorId ? parentId : undefined,
              period: period.id,
              order
            };
            
            if (parentId === visitorId) {
              Object.entries(filters).forEach(([name, value]) => {
                const column = tableColumns.find((item) => item.name === name);
                
                if (!column || math.isNone(value))
                  return;
                
                param[name] = column.getValueApiParam(column, value);
              });
            }
            
            if (result.hits === null) {
              let hits = data.hits[parentId];
              
              if (hits === undefined) {
                const stats = await api.getClassicVisitor(param, true);
                
                if (stats.locality)
                  setLocalityIds(stats.locality);
                
                data.hits[parentId] = hits = stats.hits || 0;
              }
              
              result.hits = hits;
            }
            
            param.offset = result.list.length;
            param.limit = limit;
            
            const list = result.hits ? await api.getClassicVisitor(param) : [];
            
            list.forEach((item) => {
              if (item.id !== visitorId)
                data.hits[item.id] = item.children || 0;
              
              item.canExpand = item.id !== visitorId && item.children > 0
              
              result.list.push(item);
            });
            
            result.hasNext = list.length > 0 && result.list.length < result.hits;
            result.hasNextBar = result.hasNextBar || result.hasNext;
          } catch (e) {
            console.error(e);
          }
          
          result.isLoading = false;
          result.isNextLoading = false;
          
          setData((state) => ({ ...state }));
        },
        
        prev: () => {
          //
        }
      };
      
      result.next();
    }
    
    return result;
  }, [visitorId, tableColumns, period?.id, filters, order, limit, data, setData]);
  
  const getHits = React.useCallback((parentId) => {
    return data?.hits?.[parentId] || 0;
  }, [visitorId, data]);
  
  
  React.useEffect(() => {
    getClassicVisitorColumn(visitorInfo)
      .then((list) => {
        setTableColumns(list.filter((column) => column.is_show_table));
        setDetailsColumns(list.filter((column) => column.is_show_details));
        setExportColumns(list.filter((column) => column.is_show_export));
      });
  }, [visitorId]);
  
  
  const filterGet = (columnNameSplit) => {
    const columnName = columnNameSplit[0].join('.');
    
    return filters[columnName];
  };
  
  const filterApply = (columnNameSplit, value) => {
    const columnName = columnNameSplit[0].join('.');
    
    if (value) {
      if (Array.isArray(value) && !value.length) {
        value = null;
      } else if (typeof(value) === 'object' && 'from' in value && math.isEmpty(value.from) && 'to' in value && math.isEmpty(value.to)) {
        value = null;
      }
    }
    
    setFilters((state) => ({ ...state, [columnName]: value }));
  };
  
  const showRowInfo = (rowData) => {
    setRowInfo(rowData);
  };
  
  
  return (
    <Flex flexDirection="column" gap={1} className={classes.root}>
      <FilterPeriod
        langInfo={langInfo}
        l={l}
        classes={classes}
        
        token={data.token}
        period={period}
        setPeriod={setPeriod}
      />
      
      <FilterOptions
        langInfo={langInfo}
        l={l}
        classes={classes}
        visitorInfo={visitorInfo}
        
        token={data.token}
        tableColumns={tableColumns}
        exportColumns={exportColumns}
        period={period}
        order={order}
        setOrder={setOrder}
        filters={filters}
        filterGet={filterGet}
        filterApply={filterApply}
      />
      
      <Header
        langInfo={langInfo}
        l={l}
        classes={classes}
        
        token={data.token}
        getCellStyles={getCellStyles}
        columns={tableColumns}
        setColumns={setTableColumns}
        order={order}
        setOrder={setOrder}
        filterGet={filterGet}
        filterApply={filterApply}
        localityIds={localityIds}
      />
      
      <Paper elevation={0}>
        <div className={classes.container}>
          <Rows
            langInfo={langInfo}
            l={l}
            theme={theme}
            classes={classes}
            
            token={data.token}
            getCellStyles={getCellStyles}
            columns={tableColumns}
            limit={limit}
            parent={visitorId}
            getRows={getRows}
            getHits={getHits}
            selected={selected}
            setSelected={setSelected}
            rowLevel={1}
            isRowExpanded={true}
            showRowInfo={showRowInfo}
          />
        </div>
      </Paper>
      
      <RowInfo
        langInfo={langInfo}
        l={l}
        theme={theme}
        classes={classes}
        
        token={data.token}
        detailsColumns={detailsColumns}
        rowInfo={rowInfo}
        setRowInfo={setRowInfo}
      />
    </Flex>
  );
};

RawLinear.displayName = 'RawLinear';

RawLinear.defaultProps = {
};

RawLinear.propTypes = {
  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const Linear = withLang(RawLinear);
